/** Ping our server every 10 s while the tab is visible. */
export const PING_INTERVAL_MS = 10_000;
/** A ping without an answer in 5 s counts as lost. */
export const PING_TIMEOUT_MS = 5_000;
/** Level is computed over the last N pings. */
export const SAMPLE_WINDOW = 5;

export const FAIR_MEDIAN_MS = 150;
export const POOR_MEDIAN_MS = 400;
/** Mean change between neighbouring pings that reads as an unstable link. */
export const UNSTABLE_JITTER_MS = 150;

/** External check runs only when our link looks bad, at most this often. */
export const EXTERNAL_CHECK_MIN_GAP_MS = 30_000;
export const EXTERNAL_TIMEOUT_MS = 5_000;
export const GOOGLE_CHECK_URL = "https://www.gstatic.com/generate_204";
/** By domain name: 1.1.1.1 is cut on some networks. */
export const CLOUDFLARE_TRACE_URL = "https://cloudflare.com/cdn-cgi/trace";

export type ConnectionLevel =
	| "offline"
	| "noServer"
	| "unknown"
	| "poor"
	| "unstable"
	| "fair"
	| "good";

/** Round trip in ms, `null` = lost. */
export type PingSample = number | null;

export const median = (values: number[]): number | null => {
	if (!values.length) return null;
	const sorted = [...values].sort((a, b) => a - b);
	const middle = Math.floor(sorted.length / 2);
	return sorted.length % 2
		? sorted[middle]
		: (sorted[middle - 1] + sorted[middle]) / 2;
};

/** Mean absolute change between neighbouring answered pings. */
export const jitter = (values: number[]): number | null => {
	if (values.length < 2) return null;
	let sum = 0;
	for (let i = 1; i < values.length; i++) {
		sum += Math.abs(values[i] - values[i - 1]);
	}
	return sum / (values.length - 1);
};

export interface ConnectionStats {
	level: ConnectionLevel;
	median: number | null;
	jitter: number | null;
	lost: number;
	total: number;
}

export const getConnectionStats = ({
	online,
	connected,
	samples,
}: {
	online: boolean;
	connected: boolean;
	samples: PingSample[];
}): ConnectionStats => {
	const recent = samples.slice(-SAMPLE_WINDOW);
	const answered = recent.filter((s): s is number => s !== null);
	const lost = recent.length - answered.length;
	const med = median(answered);
	const jit = answered.length >= 3 ? jitter(answered) : null;

	const stats = {
		median: med,
		jitter: jit,
		lost,
		total: recent.length,
	};

	const lastTwoLost =
		recent.length >= 2 &&
		recent[recent.length - 1] === null &&
		recent[recent.length - 2] === null;

	let level: ConnectionLevel;
	if (!online) level = "offline";
	else if (!connected || lastTwoLost) level = "noServer";
	else if (med === null) level = "unknown";
	else if (med > POOR_MEDIAN_MS) level = "poor";
	else if (jit !== null && jit >= UNSTABLE_JITTER_MS) level = "unstable";
	else if (med >= FAIR_MEDIAN_MS || lost > 0) level = "fair";
	else level = "good";

	return { level, ...stats };
};

/** Levels at which we look outside to tell whose problem it is. */
export const needsExternalCheck = (level: ConnectionLevel): boolean =>
	level === "poor" || level === "noServer" || level === "unstable";

/** `ip=` line of the Cloudflare trace text. */
export const parseCloudflareTraceIp = (text: string): string | undefined => {
	const line = text
		.split("\n")
		.map((l) => l.trim())
		.find((l) => l.startsWith("ip="));
	const ip = line?.slice(3).trim();
	return ip || undefined;
};

export type InternetVerdict = "ok" | "down" | "unknown";

/**
 * Both external sites silent: no internet only if our server is silent too.
 * Silent sites with a live server mean the network blocks them.
 */
export const getInternetVerdict = ({
	googleOk,
	cloudflareOk,
	serverReachable,
}: {
	googleOk: boolean;
	cloudflareOk: boolean;
	serverReachable: boolean;
}): InternetVerdict => {
	if (googleOk || cloudflareOk) return "ok";
	return serverReachable ? "unknown" : "down";
};
